import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { API_ORIGIN } from '@/constants'
import { authorizedFetch } from '@/lib/api'
import {
  featureLabelFromConfig,
  featureUnitFromConfig,
  planDisplayNameFromConfig,
} from '@/components/gating/gating-copy'
import type { FeatureEntitlement, GatingQuotaState } from '@/components/gating/types'
import { getUpgradePlanConfig, resolveUpgradePlanPath } from '@/constants/upgrade-plan'
import { useAuth } from './use-auth'

export type PlanCatalogEntry = {
  plan_id: string
  display_name: string
  sort_order: number
  has_stripe_price: boolean
}

export type PlanCatalog = {
  enabled: boolean
  default_plan_id: string
  plans: PlanCatalogEntry[]
}

export type OrganisationBilling = {
  organisation_slug: string
  organisation_name: string
  plan_id: string
  display_name: string
  subscription_status: string | null
  can_manage_billing: boolean
  upgrade_target: { plan_id: string; display_name: string } | null
  has_paid_plan: boolean
}

export function usePlanCatalog() {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['plans', 'catalog'],
    queryFn: async (): Promise<PlanCatalog> => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/plans/catalog`)
      if (!response.ok) {
        throw new Error('Failed to fetch plan catalog')
      }
      return response.json()
    },
    enabled: !!token,
  })
}

export function useOrganisationBilling(
  organisationSlug: string | undefined,
  options?: { sync?: boolean }
) {
  const { token } = useAuth()
  const sync = options?.sync ?? false
  return useQuery({
    queryKey: ['plans', 'billing', organisationSlug, sync ? 'sync' : 'default'],
    queryFn: async (): Promise<OrganisationBilling> => {
      if (!organisationSlug) {
        throw new Error('Organisation slug missing')
      }
      const params = sync ? '?sync=1' : ''
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/plans/organisations/${encodeURIComponent(organisationSlug)}/billing${params}`
      )
      if (!response.ok) {
        throw new Error('Failed to fetch organisation billing')
      }
      return response.json()
    },
    enabled: !!token && !!organisationSlug,
  })
}

export function useOrganisationCheckout(organisationSlug: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      planId: string
      successUrl?: string
      cancelUrl?: string
    }): Promise<{ checkout_url: string }> => {
      if (!organisationSlug) {
        throw new Error('Organisation slug missing')
      }
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/plans/organisations/${encodeURIComponent(organisationSlug)}/checkout`,
        {
          method: 'POST',
          body: JSON.stringify({
            plan_id: input.planId,
            success_url: input.successUrl,
            cancel_url: input.cancelUrl,
          }),
        }
      )
      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || 'Failed to start checkout')
      }
      return data
    },
    onSuccess: () => {
      if (organisationSlug) {
        queryClient.invalidateQueries({ queryKey: ['plans', 'billing', organisationSlug] })
        queryClient.invalidateQueries({ queryKey: ['plans', 'entitlements', organisationSlug] })
      }
    },
  })
}

export function useOrganisationPortal(organisationSlug: string | undefined) {
  return useMutation({
    mutationFn: async (): Promise<{ portal_url: string }> => {
      if (!organisationSlug) {
        throw new Error('Organisation slug missing')
      }
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/plans/organisations/${encodeURIComponent(organisationSlug)}/portal`,
        { method: 'POST', body: JSON.stringify({}) }
      )
      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || 'Failed to open billing portal')
      }
      return data
    },
  })
}

export type OrganisationEntitlements = {
  organisation_slug: string
  plans_enabled: boolean
  plan_id: string
  plan_display_name?: string
  upgrade_target?: { plan_id: string; display_name: string } | null
  features: Record<string, FeatureEntitlement>
}

/** Growth-tier AI features — granted when billing reports a paid plan. */
const GROWTH_AI_FEATURE_KEYS = new Set([
  'ai.smart_search_deep',
  'ai.semantic_prospects',
])

export function useOrganisationEntitlements(organisationSlug: string | undefined) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['plans', 'entitlements', organisationSlug],
    queryFn: async (): Promise<OrganisationEntitlements> => {
      if (!organisationSlug) {
        throw new Error('Organisation slug missing')
      }
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/plans/organisations/${encodeURIComponent(organisationSlug)}/entitlements`,
      )
      if (!response.ok) {
        throw new Error('Failed to fetch entitlements')
      }
      return response.json()
    },
    enabled: !!token && !!organisationSlug,
  })
}

function resolveUpgradePlanName(
  entitlements: OrganisationEntitlements | undefined,
  billing: OrganisationBilling | undefined,
): string {
  return (
    entitlements?.upgrade_target?.display_name ??
    billing?.upgrade_target?.display_name ??
    planDisplayNameFromConfig('paid_1')
  )
}

function quotaFromFeature(feature: FeatureEntitlement | undefined): GatingQuotaState | undefined {
  if (!feature || feature.type !== 'quota') return undefined
  const limit = feature.limit ?? 0
  const used = feature.current_count ?? 0
  return {
    limit,
    used,
    unit: feature.unit ?? featureUnitFromConfig('job_board.feature'),
    exhausted: !feature.allowed && limit > 0 && used >= limit,
  }
}

export type OrgFeatureGate = {
  allowed: boolean
  isLoading: boolean
  plansEnabled: boolean
  featureKey: string
  featureLabel: string
  planName: string
  upgradePath: string
  upgradeCtaLabel: string
  quota?: GatingQuotaState
  isQuota: boolean
}

/** Full gate state for gating primitives (tabs, peeks, fallbacks). */
export function useOrgFeatureGate(
  organisationSlug: string | undefined,
  featureKey: string,
): OrgFeatureGate {
  const {
    data: entitlements,
    isLoading: entitlementsLoading,
  } = useOrganisationEntitlements(organisationSlug)
  const { data: billing, isLoading: billingLoading } = useOrganisationBilling(organisationSlug)
  const upgrade = getUpgradePlanConfig()
  const featureLabel = featureLabelFromConfig(featureKey)
  const planName = resolveUpgradePlanName(entitlements, billing)
  const upgradePath = resolveUpgradePlanPath(organisationSlug)
  const upgradeCtaLabel = upgrade?.ctaButtonLabel ?? 'Upgrade plan'

  const base: OrgFeatureGate = {
    allowed: true,
    isLoading: false,
    plansEnabled: false,
    featureKey,
    featureLabel,
    planName,
    upgradePath,
    upgradeCtaLabel,
    isQuota: false,
  }

  if (!organisationSlug) {
    return base
  }

  const isLoading = entitlementsLoading || billingLoading
  if (isLoading) {
    return {
      ...base,
      allowed: true,
      isLoading: true,
      plansEnabled: entitlements?.plans_enabled ?? false,
    }
  }

  if (entitlements && !entitlements.plans_enabled) {
    return base
  }

  const feature = entitlements?.features[featureKey]
  const isQuota = feature?.type === 'quota'
  const quota = quotaFromFeature(feature)

  if (feature?.allowed === true) {
    return {
      ...base,
      allowed: true,
      plansEnabled: true,
      isQuota,
      quota,
    }
  }

  if (billing?.has_paid_plan && GROWTH_AI_FEATURE_KEYS.has(featureKey)) {
    return {
      ...base,
      allowed: true,
      plansEnabled: true,
      isQuota,
      quota,
    }
  }

  if (entitlements?.plans_enabled) {
    return {
      ...base,
      allowed: false,
      plansEnabled: true,
      isQuota,
      quota,
    }
  }

  return base
}

/** When plans are disabled for the tenant, all features are allowed. */
export function useOrgFeatureAllowed(
  organisationSlug: string | undefined,
  featureKey: string,
): { allowed: boolean; isLoading: boolean; plansEnabled: boolean } {
  const gate = useOrgFeatureGate(organisationSlug, featureKey)
  return {
    allowed: gate.allowed,
    isLoading: gate.isLoading,
    plansEnabled: gate.plansEnabled,
  }
}

/** Pre-gating helper for action buttons (publish, feature job, etc.). */
export function useGatedAction(
  organisationSlug: string | undefined,
  featureKey: string,
): OrgFeatureGate {
  return useOrgFeatureGate(organisationSlug, featureKey)
}
